import { define } from './define';

// The piece page (hero, variants, brief, publications, activity, agent) and the upload dialog. Keys start with "piece.".
export const piece = define({
  es: {
    'piece.crumbsPieces': 'Piezas',
    'piece.aiHint': 'Generada con IA: se etiqueta así en las redes que lo piden',
    'piece.discardedNotice': 'Pieza descartada {when}. Ya no se le pueden subir versiones ni programarla.',
    'piece.saved': 'Guardado',
    'piece.uploadVersion': 'Subir versión',
    'piece.uploadVersionHint': 'Sube una versión nueva de una variante y mándala a revisión',
    'piece.reviewN': 'Revisar v{n}',
    'piece.reviewHint': 'Abre la v{n} de {variant} para verla, comentarla y aprobarla',
    'piece.uploadFirst': 'Subir la primera',
    'piece.unknownAuthor': 'Alguien',
    'piece.versionTitle': 'v{n} · {state} · {who} · {when}',
    'piece.openComments': { one: '{count} comentario abierto', other: '{count} comentarios abiertos' },
    'piece.noOpenComments': 'Sin comentarios abiertos',
    'piece.noPreview': 'Sin vista previa',
    'piece.pubs': 'Publicaciones',

    'piece.menu.label': 'Más acciones de la pieza',
    'piece.menu.edit': 'Editar datos',
    'piece.menu.editHint': 'Título, brief, campaña, fecha objetivo, origen del proyecto y etiqueta de IA',
    'piece.menu.move': 'Mover a otra campaña…',
    'piece.menu.moveHint': 'Cambia la campaña en la que está la pieza',
    'piece.menu.discard': 'Descartar la pieza…',
    'piece.menu.discardHint': 'Deja de estar en revisión y se cancela lo que tenga programado',

    'piece.hero.label': 'La pieza',
    'piece.hero.facts': 'Datos de la pieza',
    'piece.hero.play': 'Reproducir',
    'piece.hero.prev': 'Anterior',
    'piece.hero.next': 'Siguiente',
    'piece.hero.slides': { one: '{count} imagen', other: '{count} imágenes' },
    'piece.hero.pages': { one: '{count} página', other: '{count} páginas' },
    'piece.hero.page': 'Página {n}',
    'piece.hero.morePages': { one: 'Y {count} página más: ábrela en la revisión', other: 'Y {count} páginas más: ábrelas en la revisión' },
    'piece.hero.pdf': 'PDF',
    'piece.hero.pdfPages': { one: 'PDF · {count} pág.', other: 'PDF · {count} págs.' },
    'piece.hero.videoOf': 'Vídeo de la v{n}',
    'piece.hero.noVersion': 'Todavía no hay ninguna versión de esta variante',
    'piece.hero.openReview': 'Revisar la v{n} a pantalla completa (R)',
    'piece.hero.versionsOf': 'Versiones de {variant}',
    'piece.hero.latest': 'Última',
    'piece.hero.approvedBy': 'Aprobada por {names}',
    'piece.aiLabel': 'Generada con IA',

    'piece.fields.title': 'Título',
    'piece.fields.campaign': 'Campaña',
    'piece.fields.noCampaign': 'Sin campaña',
    'piece.fields.target': 'Fecha objetivo',
    'piece.fields.noDate': 'Sin fecha',
    'piece.fields.source': 'Origen del proyecto',
    'piece.fields.sourceWhat': '¿Qué es el origen del proyecto?',
    'piece.fields.sourceHint': 'Dónde vive el proyecto del que sale esta pieza: el código y el material con que se hace. Cuando el agente prepara una versión nueva trabaja ahí, en su propia rama. Se escribe como repositorio:carpeta, por ejemplo videos:2026-09-29-trimestre/telenovela.',
    'piece.fields.sourcePlaceholder': 'videos:2026-09-29-trimestre/telenovela',
    'piece.fields.addSource': 'Añadir origen',
    'piece.fields.noSource': 'Sin origen',
    'piece.inline.edit': 'Cambiar {what}',

    'piece.brief': 'Brief',
    'piece.briefEdit': 'Editar el brief',
    'piece.briefMore': 'Ver el brief entero',
    'piece.briefLess': 'Ver menos',
    'piece.briefEmpty': 'Esta pieza todavía no tiene brief.',
    'piece.briefEmptyEdit': 'Esta pieza todavía no tiene brief. Escribe para qué es: es lo primero que lee quien la produce.',

    'piece.variants': 'Variantes',
    'piece.noVariants': 'Todavía no hay variantes',
    'piece.noVariantsUpload': 'Añade una variante para elegir el formato y después sube la primera versión.',
    'piece.noVariantsWait': 'Quien produce esta pieza añadirá los formatos.',
    'piece.variant.show': 'Ver {variant} arriba',
    'piece.variant.upload': 'Subir una versión de {variant}',
    'piece.variant.empty': 'Sin versiones',

    'piece.formatUse.9:16': 'Reels, Shorts, TikTok, Stories',
    'piece.formatUse.4:5': 'Vertical para el feed',
    'piece.formatUse.1:1': 'Cuadrado',
    'piece.formatUse.16:9': 'Horizontal: YouTube, LinkedIn',
    'piece.formatUse.carousel': 'De 2 a 20 imágenes o vídeos',
    'piece.formatUse.document': 'Un PDF para LinkedIn o un premio',

    'piece.addVariant.button': 'Añadir variante',
    'piece.addVariant.hint': 'La misma pieza en otro formato o con otro estilo, que se revisa aparte',
    'piece.addVariant.title': 'Añadir una variante',
    'piece.addVariant.format': 'Formato',
    'piece.addVariant.style': 'Estilo (opcional)',
    'piece.addVariant.styleHint': 'Lo que distingue esta variante de las demás del mismo formato: el estilo visual («Suiza», «Riso», «Telenovela») o el montaje («Montaje final», «Teaser»).',
    'piece.addVariant.stylePlaceholder': 'Por ejemplo: Riso',
    'piece.addVariant.submit': 'Añadir variante',
    'piece.addVariant.done': 'Variante añadida',

    'piece.edit.title': 'Editar los datos de la pieza',
    'piece.edit.fieldTitle': 'Título',
    'piece.edit.brief': 'Brief',
    'piece.edit.target': 'Fecha objetivo',
    'piece.edit.sourceHint': 'repositorio:carpeta. El agente trabaja ahí; vacío si la pieza no sale de un proyecto.',
    'piece.edit.ai': 'Generada con IA',
    'piece.edit.done': 'Cambios guardados',

    'piece.move.title': 'Mover a otra campaña',
    'piece.move.now': 'Ahora',
    'piece.move.submit': 'Mover',
    'piece.move.done': 'Movida a «{name}»',
    'piece.move.doneNone': 'Ya no está en ninguna campaña',

    'piece.discard.title': 'Descartar la pieza',
    'piece.discard.body': '«{title}» deja de estar en revisión y ya no se le pueden subir versiones.',
    'piece.discard.cancels': { one: 'También se cancela la publicación que tiene pendiente.', other: 'También se cancelan sus {count} publicaciones pendientes.' },
    'piece.discard.submit': 'Descartar la pieza',
    'piece.discard.done': 'Pieza descartada',

    'piece.act.title': 'Actividad',
    'piece.act.empty': 'Todavía no ha pasado nada con esta pieza.',
    'piece.act.all': 'Ver toda la actividad ({count})',
    'piece.act.less': 'Ver menos',
    'piece.act.created': 'creó la pieza',
    'piece.act.discarded': 'descartó la pieza',
    'piece.act.reset': 'devolvió la pieza al agente',
    'piece.act.edited': 'cambió {fields}',
    'piece.act.editedSome': 'editó los datos de la pieza',
    'piece.act.field.title': 'el título',
    'piece.act.field.brief': 'el brief',
    'piece.act.field.target_date': 'la fecha objetivo',
    'piece.act.field.ai_generated': 'la etiqueta de IA',
    'piece.act.field.source': 'el origen del proyecto',
    'piece.act.version': 'subió la {v}',
    'piece.act.comment': 'comentó en la {v}',
    'piece.act.reply': 'respondió en la {v}',
    'piece.act.approved': 'aprobó la {v}',
    'piece.act.rejected': 'rechazó la {v}',
    'piece.act.changes': 'pidió cambios en la {v}',
    'piece.act.published': 'Se publicó la {v} en {network}',
    'piece.act.failed': 'No se pudo publicar la {v} en {network}',
    'piece.act.page': 'pág. {n}',

    'piece.agent.title': 'Agente',
    'piece.agent.running': 'Trabajando',
    'piece.agent.needsPerson': 'Necesita a una persona',
    'piece.agent.idle': 'Sin tareas',
    'piece.agent.rounds': 'Rondas',
    'piece.agent.spent': 'Gastado',
    'piece.agent.stopped': 'El agente se ha parado',
    'piece.agent.handBack': 'Devolvérsela al agente',
    'piece.agent.handBackHint': 'Cuenta otra vez desde cero las rondas y el gasto de esta pieza, y le manda al agente la petición que está esperando',
    'piece.agent.handedBack': 'Devuelta al agente',
    'piece.agent.adminBudget': 'Un administrador puede fijar o subir el presupuesto en Ajustes → Agente.',
    'piece.agent.runs': 'Lo que ha hecho',
    'piece.agent.openVersion': 'Abrir la v{n}',
    'piece.agent.notCounted': 'no cuenta',
    'piece.agent.notCountedHint': 'No cuenta para las rondas ni para el presupuesto',
    'piece.agent.checks': 'Comprobaciones: {summary}',
    'piece.agent.more': { one: 'Ver {count} intento anterior', other: 'Ver {count} intentos anteriores' },
    'piece.agent.fewer': 'Ver menos',
    'piece.agent.project.source': 'Origen del proyecto en el que trabajó',
    'piece.agent.project.dir': 'Trabajó en la carpeta, sin commit',
    'piece.agent.project.branch': 'Rama de la pieza en el repositorio',
    'piece.agent.project.commit': 'Commit {sha}',
    'piece.agent.project.unchanged': 'Sin cambios en el proyecto',
    'piece.agent.project.files': { one: '{count} fichero', other: '{count} ficheros' },
    'piece.agent.project.pushed': 'Subido',
    'piece.agent.project.notPushed': 'Sin subir',
    'piece.agent.project.notPushedHint': 'El commit está en la máquina del agente, pero no en el repositorio remoto',

    'piece.upload.title': 'Subir una versión nueva',
    'piece.upload.willBe': 'será la v{n}',
    'piece.upload.step.variant': '¿De qué variante es?',
    'piece.upload.variantNow': 'Ahora en v{n} · esta será la v{next}',
    'piece.upload.variantFirst': 'Sin versiones · esta será la v1',
    'piece.upload.step.files': 'Ficheros',
    'piece.upload.step.text': 'Texto de la publicación',
    'piece.upload.textPlaceholder': 'El texto tal como saldrá en la red. Se revisa y se aprueba junto con los archivos.',
    'piece.upload.textHint': 'Forma parte de la versión: si cambia una palabra, la aprobación deja de contar.',
    'piece.upload.step.notes': 'Qué ha cambiado',
    'piece.upload.hint.document': 'Un PDF.',
    'piece.upload.hint.carousel': 'De 2 a 20 imágenes o vídeos, en el orden en que deben salir. Un vídeo puede llevar además portada y subtítulos.',
    'piece.upload.hint.default': 'Un vídeo o una imagen. Un vídeo puede llevar además una imagen de portada y subtítulos (.vtt o .srt).',
    'piece.upload.drop': 'Arrastra aquí los ficheros',
    'piece.upload.dropOne': 'Arrastra aquí el PDF',
    'piece.upload.dropMore': 'Arrastra aquí más ficheros',
    'piece.upload.choose': 'Elegir ficheros',
    'piece.upload.chooseOne': 'Elegir el PDF',
    'piece.upload.chooseOther': 'Elegir otro PDF',
    'piece.upload.filesLabel': { one: '{count} fichero', other: '{count} ficheros' },
    'piece.upload.roleOf': 'Papel de {name}',
    'piece.upload.moveUp': 'Subir {name} un puesto',
    'piece.upload.moveUpShort': 'Antes',
    'piece.upload.moveDown': 'Bajar {name} un puesto',
    'piece.upload.moveDownShort': 'Después',
    'piece.upload.remove': 'Quitar {name}',
    'piece.upload.removeShort': 'Quitar',
    'piece.upload.progressOf': 'Progreso de {name}',
    'piece.upload.pageMark': 'pág. {n}',
    'piece.upload.notesPlaceholder': 'Por ejemplo: «voz nueva en el 0:05 y sombra bajo los pies de Iván»',
    'piece.upload.fixes': 'Comentarios que arregla esta versión',
    'piece.upload.fixFrom': 'v{n} · {who}',
    'piece.upload.summary': { one: '{count} fichero · {size}', other: '{count} ficheros · {size}' },
    'piece.upload.failedFile': 'Un fichero no se ha podido subir: tienes el error junto a él, en la lista.',
    'piece.upload.sending': 'Subiendo…',
    'piece.upload.submit': 'Subir y mandar a revisión',
    'piece.upload.done': 'Versión subida y enviada a revisión',
    'piece.formatName.carousel': 'Carrusel',
    'piece.formatName.document': 'PDF',
    'piece.formatHint.9:16': 'Vertical',
    'piece.formatHint.4:5': 'Vertical para el feed',
    'piece.formatHint.1:1': 'Cuadrado',
    'piece.formatHint.16:9': 'Horizontal',
    'piece.formatHint.carousel': 'Diapositivas',
    'piece.formatHint.document': 'Documento',
    'piece.agent.why.budget_not_set': 'Todavía no hay presupuesto para el agente, así que no ha empezado.',
    'piece.agent.why.rounds_exhausted': 'Ha usado sus {max} rondas en esta pieza. A partir de aquí sigue una persona.',
    'piece.agent.why.piece_budget_reached': 'Ha llegado al presupuesto de esta pieza ({spent} de {cap}).',
    'piece.agent.why.monthly_budget_reached': 'Ha llegado al presupuesto del mes ({spent} de {cap}).',
    'piece.upload.role.video': 'Vídeo',
    'piece.upload.role.image': 'Imagen',
    'piece.upload.role.pdf': 'PDF',
    'piece.upload.role.cover': 'Portada',
    'piece.upload.role.subtitles': 'Subtítulos',
    'piece.upload.phase.waiting': 'En espera',
    'piece.upload.phase.checking': 'Comprobando… {pct} %',
    'piece.upload.phase.checked': 'Comprobado, en cola',
    'piece.upload.phase.uploading': 'Subiendo… {pct} %',
    'piece.upload.phase.done': 'Subido',
    'piece.upload.problem.empty': 'El fichero está vacío.',
    'piece.upload.problem.tooBig': 'Pesa demasiado: el máximo es {max}.',
    'piece.upload.problem.type': 'Este tipo de fichero no vale como «{role}».',
    'piece.upload.rule.tooMany': 'Como mucho {max} ficheros por versión.',
    'piece.upload.rule.oneCover': 'Solo puede haber una portada.',
    'piece.upload.rule.tooManySubtitles': 'Demasiados ficheros de subtítulos: como mucho 10.',
    'piece.upload.rule.subtitlesNeedVideo': 'Los subtítulos solo valen con un vídeo.',
    'piece.upload.rule.document': 'Esta variante lleva un solo PDF y nada más.',
    'piece.upload.rule.carouselNoPdf': 'Un carrusel no lleva PDF.',
    'piece.upload.rule.carouselCount': 'Un carrusel lleva entre 2 y 20 imágenes o vídeos; ahora hay {count}.',
    'piece.upload.rule.noPdf': 'Esta variante no lleva PDF.',
    'piece.upload.rule.single': 'Esta variante lleva un solo vídeo o una sola imagen.',
    'piece.upload.rule.singleCover': 'Esta variante lleva un solo vídeo o una sola imagen. Si la imagen es la portada del vídeo, cambia su papel a «Portada».',
    'piece.upload.rule.coverNeedsVideo': 'La portada solo vale con un vídeo.',
    'piece.upload.status.checking': 'Comprobando el fichero {n} de {total}…',
    'piece.upload.status.uploading': 'Subiendo el fichero {n} de {total}… {pct} %',
    'piece.upload.status.closing': 'Creando la versión…',
    'piece.upload.err.storage': 'La subida ha fallado ({status}): {detail}',
    'piece.upload.err.network': 'La subida ha fallado: no hay conexión',
    'piece.upload.err.keepsFailing': 'La conexión sigue fallando',
    'piece.upload.err.gaveUp': '{what}. Elige el mismo fichero otra vez para seguir donde se quedó.',
    'piece.upload.err.retrying': 'problema de conexión, reintentando',
    'piece.upload.err.offset': 'El servidor y el navegador no se ponen de acuerdo en dónde acaba el fichero. Elígelo otra vez.',
    'piece.upload.err.refused': 'Se ha rechazado la subida ({status})',
    'piece.upload.err.unconfirmed': 'El servidor no ha confirmado el fichero. Elige el mismo fichero otra vez para terminarlo.',
    'piece.upload.err.incomplete': 'El fichero no se ha podido completar. Elígelo otra vez para seguir.',
  },
  en: {
    'piece.crumbsPieces': 'Pieces',
    'piece.aiHint': 'Generated with AI: labelled as such on networks that ask for it',
    'piece.discardedNotice': 'Piece discarded {when}. No more versions can be uploaded or scheduled.',
    'piece.saved': 'Saved',
    'piece.uploadVersion': 'Upload version',
    'piece.uploadVersionHint': 'Upload a new version of a variant and send it to review',
    'piece.reviewN': 'Review v{n}',
    'piece.reviewHint': 'Open v{n} of {variant} to watch, comment on and approve it',
    'piece.uploadFirst': 'Upload the first',
    'piece.unknownAuthor': 'Someone',
    'piece.versionTitle': 'v{n} · {state} · {who} · {when}',
    'piece.openComments': { one: '{count} open comment', other: '{count} open comments' },
    'piece.noOpenComments': 'No open comments',
    'piece.noPreview': 'No preview',
    'piece.pubs': 'Publications',

    'piece.menu.label': 'More actions for the piece',
    'piece.menu.edit': 'Edit details',
    'piece.menu.editHint': 'Title, brief, campaign, target date, project source and AI label',
    'piece.menu.move': 'Move to another campaign…',
    'piece.menu.moveHint': 'Change the campaign the piece belongs to',
    'piece.menu.discard': 'Discard the piece…',
    'piece.menu.discardHint': 'It leaves review and anything scheduled is cancelled',

    'piece.hero.label': 'The piece',
    'piece.hero.facts': 'Piece details',
    'piece.hero.play': 'Play',
    'piece.hero.prev': 'Previous',
    'piece.hero.next': 'Next',
    'piece.hero.slides': { one: '{count} image', other: '{count} images' },
    'piece.hero.pages': { one: '{count} page', other: '{count} pages' },
    'piece.hero.page': 'Page {n}',
    'piece.hero.morePages': { one: 'And {count} more page: open it in review', other: 'And {count} more pages: open them in review' },
    'piece.hero.pdf': 'PDF',
    'piece.hero.pdfPages': { one: 'PDF · {count} page', other: 'PDF · {count} pages' },
    'piece.hero.videoOf': 'Video of v{n}',
    'piece.hero.noVersion': 'This variant has no versions yet',
    'piece.hero.openReview': 'Review v{n} full screen (R)',
    'piece.hero.versionsOf': 'Versions of {variant}',
    'piece.hero.latest': 'Latest',
    'piece.hero.approvedBy': 'Approved by {names}',
    'piece.aiLabel': 'Made with AI',

    'piece.fields.title': 'Title',
    'piece.fields.campaign': 'Campaign',
    'piece.fields.noCampaign': 'No campaign',
    'piece.fields.target': 'Target date',
    'piece.fields.noDate': 'No date',
    'piece.fields.source': 'Project source',
    'piece.fields.sourceWhat': 'What is the project source?',
    'piece.fields.sourceHint': 'Where the project this piece is made from lives: its code and material. When the agent prepares a new version it works there, on a branch of its own. Written as repository:folder, for example videos:2026-09-29-quarter/telenovela.',
    'piece.fields.sourcePlaceholder': 'videos:2026-09-29-quarter/telenovela',
    'piece.fields.addSource': 'Add source',
    'piece.fields.noSource': 'No source',
    'piece.inline.edit': 'Change {what}',

    'piece.brief': 'Brief',
    'piece.briefEdit': 'Edit the brief',
    'piece.briefMore': 'Show the whole brief',
    'piece.briefLess': 'Show less',
    'piece.briefEmpty': 'This piece has no brief yet.',
    'piece.briefEmptyEdit': 'This piece has no brief yet. Write what it is for: it is the first thing whoever produces it reads.',

    'piece.variants': 'Variants',
    'piece.noVariants': 'No variants yet',
    'piece.noVariantsUpload': 'Add a variant to choose the format, then upload the first version.',
    'piece.noVariantsWait': 'Whoever produces this piece will add the formats.',
    'piece.variant.show': 'Show {variant} above',
    'piece.variant.upload': 'Upload a version of {variant}',
    'piece.variant.empty': 'No versions',

    'piece.formatUse.9:16': 'Reels, Shorts, TikTok, Stories',
    'piece.formatUse.4:5': 'Portrait, for the feed',
    'piece.formatUse.1:1': 'Square',
    'piece.formatUse.16:9': 'Landscape: YouTube, LinkedIn',
    'piece.formatUse.carousel': '2 to 20 images or videos',
    'piece.formatUse.document': 'A PDF for LinkedIn or a prize',

    'piece.addVariant.button': 'Add variant',
    'piece.addVariant.hint': 'The same piece in another format or style, reviewed on its own',
    'piece.addVariant.title': 'Add a variant',
    'piece.addVariant.format': 'Format',
    'piece.addVariant.style': 'Style (optional)',
    'piece.addVariant.styleHint': 'What sets this variant apart from the others in the same format: its visual style (“Suiza”, “Riso”, “Telenovela”) or its cut (“Final cut”, “Teaser”).',
    'piece.addVariant.stylePlaceholder': 'For example: Riso',
    'piece.addVariant.submit': 'Add variant',
    'piece.addVariant.done': 'Variant added',

    'piece.edit.title': 'Edit the piece’s details',
    'piece.edit.fieldTitle': 'Title',
    'piece.edit.brief': 'Brief',
    'piece.edit.target': 'Target date',
    'piece.edit.sourceHint': 'repository:folder. The agent works there; empty if the piece is not made from a project.',
    'piece.edit.ai': 'Generated with AI',
    'piece.edit.done': 'Changes saved',

    'piece.move.title': 'Move to another campaign',
    'piece.move.now': 'Now',
    'piece.move.submit': 'Move',
    'piece.move.done': 'Moved to “{name}”',
    'piece.move.doneNone': 'It is no longer in a campaign',

    'piece.discard.title': 'Discard this piece',
    'piece.discard.body': '“{title}” leaves review and no more versions can be uploaded to it.',
    'piece.discard.cancels': { one: 'Its pending publication is cancelled too.', other: 'Its {count} pending publications are cancelled too.' },
    'piece.discard.submit': 'Discard the piece',
    'piece.discard.done': 'Piece discarded',

    'piece.act.title': 'Activity',
    'piece.act.empty': 'Nothing has happened with this piece yet.',
    'piece.act.all': 'Show all activity ({count})',
    'piece.act.less': 'Show less',
    'piece.act.created': 'created the piece',
    'piece.act.discarded': 'discarded the piece',
    'piece.act.reset': 'handed the piece back to the agent',
    'piece.act.edited': 'changed {fields}',
    'piece.act.editedSome': 'edited the piece’s details',
    'piece.act.field.title': 'the title',
    'piece.act.field.brief': 'the brief',
    'piece.act.field.target_date': 'the target date',
    'piece.act.field.ai_generated': 'the AI label',
    'piece.act.field.source': 'the project source',
    'piece.act.version': 'uploaded {v}',
    'piece.act.comment': 'commented on {v}',
    'piece.act.reply': 'replied on {v}',
    'piece.act.approved': 'approved {v}',
    'piece.act.rejected': 'rejected {v}',
    'piece.act.changes': 'asked for changes on {v}',
    'piece.act.published': '{v} was published on {network}',
    'piece.act.failed': '{v} could not be published on {network}',
    'piece.act.page': 'p. {n}',

    'piece.agent.title': 'Agent',
    'piece.agent.running': 'Working on it',
    'piece.agent.needsPerson': 'Needs a person',
    'piece.agent.idle': 'Idle',
    'piece.agent.rounds': 'Rounds',
    'piece.agent.spent': 'Spent',
    'piece.agent.stopped': 'The agent stopped',
    'piece.agent.handBack': 'Hand it back to the agent',
    'piece.agent.handBackHint': 'Counts rounds and spending on this piece from zero again, and sends the request that is waiting to the agent',
    'piece.agent.handedBack': 'Handed back to the agent',
    'piece.agent.adminBudget': 'An admin can set or raise the budget in Settings → Agent.',
    'piece.agent.runs': 'What it did',
    'piece.agent.openVersion': 'Open v{n}',
    'piece.agent.notCounted': 'not counted',
    'piece.agent.notCountedHint': 'Counts towards neither the rounds nor the budget',
    'piece.agent.checks': 'Checks: {summary}',
    'piece.agent.more': { one: 'Show {count} earlier run', other: 'Show {count} earlier runs' },
    'piece.agent.fewer': 'Show fewer',
    'piece.agent.project.source': 'Source of the project it worked in',
    'piece.agent.project.dir': 'Worked in the folder, no commit',
    'piece.agent.project.branch': 'The piece’s branch in the repository',
    'piece.agent.project.commit': 'Commit {sha}',
    'piece.agent.project.unchanged': 'No changes to the project',
    'piece.agent.project.files': { one: '{count} file', other: '{count} files' },
    'piece.agent.project.pushed': 'Pushed',
    'piece.agent.project.notPushed': 'Not pushed',
    'piece.agent.project.notPushedHint': 'The commit is on the agent’s machine but not in the remote repository',

    'piece.upload.title': 'Upload a new version',
    'piece.upload.willBe': 'will be v{n}',
    'piece.upload.step.variant': 'Which variant is it for?',
    'piece.upload.variantNow': 'Now at v{n} · this will be v{next}',
    'piece.upload.variantFirst': 'No versions · this will be v1',
    'piece.upload.step.files': 'Files',
    'piece.upload.step.text': 'Text of the post',
    'piece.upload.textPlaceholder': 'The text as it should go out on the network. It is reviewed and approved with the files.',
    'piece.upload.textHint': 'It is part of the version: change a word and the approval stops counting.',
    'piece.upload.step.notes': 'What changed',
    'piece.upload.hint.document': 'One PDF.',
    'piece.upload.hint.carousel': '2 to 20 images or videos, in the order they should appear. A video can also have a cover and subtitles.',
    'piece.upload.hint.default': 'One video or one image. A video can also have a cover image and subtitle files (.vtt or .srt).',
    'piece.upload.drop': 'Drag the files here',
    'piece.upload.dropOne': 'Drag the PDF here',
    'piece.upload.dropMore': 'Drag more files here',
    'piece.upload.choose': 'Choose files',
    'piece.upload.chooseOne': 'Choose the PDF',
    'piece.upload.chooseOther': 'Choose another PDF',
    'piece.upload.filesLabel': { one: '{count} file', other: '{count} files' },
    'piece.upload.roleOf': 'Role of {name}',
    'piece.upload.moveUp': 'Move {name} up',
    'piece.upload.moveUpShort': 'Earlier',
    'piece.upload.moveDown': 'Move {name} down',
    'piece.upload.moveDownShort': 'Later',
    'piece.upload.remove': 'Remove {name}',
    'piece.upload.removeShort': 'Remove',
    'piece.upload.progressOf': 'Progress of {name}',
    'piece.upload.pageMark': 'p. {n}',
    'piece.upload.notesPlaceholder': 'For example: “new voice at 0:05 and a shadow under Iván’s feet”',
    'piece.upload.fixes': 'Comments this version fixes',
    'piece.upload.fixFrom': 'v{n} · {who}',
    'piece.upload.summary': { one: '{count} file · {size}', other: '{count} files · {size}' },
    'piece.upload.failedFile': 'A file could not be uploaded: its error is next to it in the list.',
    'piece.upload.sending': 'Uploading…',
    'piece.upload.submit': 'Upload and send to review',
    'piece.upload.done': 'Version uploaded and sent to review',
    'piece.formatName.carousel': 'Carousel',
    'piece.formatName.document': 'PDF',
    'piece.formatHint.9:16': 'Vertical',
    'piece.formatHint.4:5': 'Portrait feed',
    'piece.formatHint.1:1': 'Square',
    'piece.formatHint.16:9': 'Landscape',
    'piece.formatHint.carousel': 'Slides',
    'piece.formatHint.document': 'Document',
    'piece.agent.why.budget_not_set': 'There is no budget for the agent yet, so it did not start.',
    'piece.agent.why.rounds_exhausted': 'It has used its {max} rounds on this piece. A person has to take it from here.',
    'piece.agent.why.piece_budget_reached': 'It has reached the budget for this piece ({spent} of {cap}).',
    'piece.agent.why.monthly_budget_reached': 'It has reached this month’s budget ({spent} of {cap}).',
    'piece.upload.role.video': 'Video',
    'piece.upload.role.image': 'Image',
    'piece.upload.role.pdf': 'PDF',
    'piece.upload.role.cover': 'Cover',
    'piece.upload.role.subtitles': 'Subtitles',
    'piece.upload.phase.waiting': 'Waiting',
    'piece.upload.phase.checking': 'Checking… {pct}%',
    'piece.upload.phase.checked': 'Checked, queued',
    'piece.upload.phase.uploading': 'Uploading… {pct}%',
    'piece.upload.phase.done': 'Uploaded',
    'piece.upload.problem.empty': 'The file is empty.',
    'piece.upload.problem.tooBig': 'Too big: the most is {max}.',
    'piece.upload.problem.type': 'This kind of file cannot be used as “{role}”.',
    'piece.upload.rule.tooMany': 'At most {max} files per version.',
    'piece.upload.rule.oneCover': 'There can be only one cover.',
    'piece.upload.rule.tooManySubtitles': 'Too many subtitle files: 10 at most.',
    'piece.upload.rule.subtitlesNeedVideo': 'Subtitles only apply to a video.',
    'piece.upload.rule.document': 'This variant takes exactly one PDF and nothing else.',
    'piece.upload.rule.carouselNoPdf': 'A carousel takes no PDF.',
    'piece.upload.rule.carouselCount': 'A carousel takes between 2 and 20 images or videos; there are {count}.',
    'piece.upload.rule.noPdf': 'This variant takes no PDF.',
    'piece.upload.rule.single': 'This variant takes exactly one video or one image.',
    'piece.upload.rule.singleCover': 'This variant takes exactly one video or one image. If the image is the video’s cover, set its role to “Cover”.',
    'piece.upload.rule.coverNeedsVideo': 'A cover only applies to a video.',
    'piece.upload.status.checking': 'Checking file {n} of {total}…',
    'piece.upload.status.uploading': 'Uploading file {n} of {total}… {pct}%',
    'piece.upload.status.closing': 'Creating the version…',
    'piece.upload.err.storage': 'Upload failed ({status}): {detail}',
    'piece.upload.err.network': 'Upload failed: network error',
    'piece.upload.err.keepsFailing': 'The connection keeps failing',
    'piece.upload.err.gaveUp': '{what}. Choose the same file again to carry on from where it stopped.',
    'piece.upload.err.retrying': 'connection problem, trying again',
    'piece.upload.err.offset': 'The server and the browser cannot agree where the file ends. Choose the file again.',
    'piece.upload.err.refused': 'The upload was refused ({status})',
    'piece.upload.err.unconfirmed': 'The server did not confirm the file. Choose the same file again to finish it.',
    'piece.upload.err.incomplete': 'The file could not be completed. Choose it again to carry on.',
  },
});
